import React, { useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  LayoutDashboard,
  TrendingUp,
  FileText,
  Anchor,
  Package,
  DollarSign,
  Brain,
  ChevronDown,
  ChevronLeft,
  ShoppingCart,
  ShoppingBag,
  Truck,
  BookOpen,
  Users,
  BarChart3,
  Calculator,
  Settings,
  Sparkles,
  Ship,
  Grid3X3,
  Code2,
  MessageSquareMore,
  Bot,
  FileQuestion,
  PanelLeftClose,
  PanelLeftOpen,
  X,
  Bell,
  Building2,
  Tag,
  Receipt,
  CreditCard,
  BookMarked,
  Wallet,
  PieChart,
  MapPin,
  Map,
  ScrollText,
  Globe,
  Download,
  Compass,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

// ─── Navigation style tokens ──────────────────────────────────────────────────
const NAV_STYLES = {
  activeMain: "bg-white/5 text-white font-medium",
  activeSub: "bg-white/5 text-white",
  hover: "text-[#9CA3AF] hover:bg-white/5 hover:text-white",
  dot: "before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[3px] before:rounded-r-full before:bg-gradient-to-b before:from-blue-600 before:via-blue-500 before:to-teal-400",
};

// ─── NavItem (main level) ─────────────────────────────────────────────────────
const NavItem = ({
  id,
  label,
  icon: Icon,
  activeView,
  externalLink,
  isCollapsed = false,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const isActive = activeView === id;

  const handleClick = useCallback(() => {
    if (externalLink) {
      window.open(externalLink, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/${id}`);
      if (onNavigate) onNavigate();
    }
  }, [id, externalLink, navigate, onNavigate]);

  return (
    <div className="relative group">
      <button
        onClick={handleClick}
        data-testid={`nav-${id}`}
        className={`relative w-full flex items-center ${isCollapsed ? "justify-center px-2" : "space-x-3 px-3"
          } py-2.5 rounded-lg transition-all duration-150 ${isActive
            ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}`
            : NAV_STYLES.hover
          }`}
      >
        <Icon
          className="w-[18px] h-[18px] flex-shrink-0"
          strokeWidth={isActive ? 2.2 : 1.8}
        />
        {!isCollapsed && (
          <span className="text-sm text-left leading-none">{label}</span>
        )}
        {!isCollapsed && externalLink && (
          <svg
            className="w-3 h-3 ml-auto opacity-50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        )}
      </button>
      {isCollapsed && (
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
        </div>
      )}
    </div>
  );
};

// ─── ParentNavBtn (top-level parent) ─────────────────────────────────────────
const ParentNavBtn = ({
  icon: Icon,
  label,
  isActive,
  isOpen,
  isCollapsed,
  tooltip,
  onClick,
  testId,
}) => (
  <div className="relative group">
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-full flex items-center ${isCollapsed ? "justify-center px-2" : "justify-between px-3"
        } py-2.5 rounded-lg transition-all duration-150 ${isActive
          ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}`
          : NAV_STYLES.hover
        }`}
    >
      <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
        <Icon
          className="w-[18px] h-[18px] flex-shrink-0"
          strokeWidth={isActive ? 2.2 : 1.8}
        />
        {!isCollapsed && <span className="font-medium text-sm">{label}</span>}
      </div>
      {!isCollapsed && (
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform duration-200 opacity-60 ${isOpen ? "rotate-0" : "-rotate-90"
            }`}
          strokeWidth={2.5}
        />
      )}
    </button>
    {isCollapsed && (
      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
        {tooltip || label}
        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
      </div>
    )}
  </div>
);

// ─── TreeMenu (outer vertical line) ──────────────────────────────────────────
const TreeMenu = ({ children, showLine = true }) => (
  <div className="relative mt-1 mb-1">
    {showLine && (
      <span
        aria-hidden
        className="absolute top-0 bottom-1 left-[25px] w-px bg-white/40 z-10 pointer-events-none"
      />
    )}
    {children}
  </div>
);

// ─── TreeItem (submenu leaf) ─────────────────────────────────────────────────
const TreeItem = ({
  id,
  label,
  activeView,
  onNavigate,
  testId,
  indent = "pl-9",
}) => {
  const navigate = useNavigate();
  const isActive =
    activeView === id ||
    activeView === id.replace("/", "-") ||
    "/" + activeView === "/" + id;

  const handleClick = useCallback(() => {
    navigate(`/${id}`);
    if (onNavigate) onNavigate();
  }, [id, navigate, onNavigate]);

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        data-testid={testId || `nav-${id.replace("/", "-")}`}
        className={`relative w-full flex items-center gap-2 ${indent} pr-3 py-2 rounded-lg transition-all duration-150 text-sm ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover
          }`}
      >
        <span
          className={`text-xs flex-shrink-0 ${isActive ? "text-blue-400" : "text-white/20"
            }`}
        >
          —
        </span>
        <span>{label}</span>
      </button>
    </div>
  );
};

// ─── TreeParent (submenu parent, e.g. Export/Import) ─────────────────────────
const TreeParent = ({
  icon: Icon,
  label,
  isActive,
  isOpen,
  onClick,
  children,
  testId,
}) => (
  <div className="relative">
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative w-full flex items-center justify-between pl-9 pr-3 py-2 rounded-lg transition-all duration-150 text-sm ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover
        }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`text-xs flex-shrink-0 ${isActive ? "text-blue-400" : "text-white/20"
            }`}
        >
          —
        </span>
        <span className="font-medium">{label}</span>
      </div>
      <ChevronDown
        className={`w-3 h-3 opacity-60 transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"
          }`}
        strokeWidth={2.5}
      />
    </button>

    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="overflow-hidden"
        >
          <div className="relative">
            <span
              aria-hidden
              className="absolute top-0 bottom-1 left-[48px] w-px bg-white/40 z-10 pointer-events-none"
            />
            <div className="pl-4">{children}</div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ─── NavContent ───────────────────────────────────────────────────────────────
const NavContent = ({ isCollapsed, onNavigate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAIInsightsOpen, setIsAIInsightsOpen] = useState(false);
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const [isSystemMaintOpen, setIsSystemMaintOpen] = useState(false);
  const [isAccountingOpen, setIsAccountingOpen] = useState(false);
  const [isOceanOpen, setIsOceanOpen] = useState(false);
  const [isOceanExportOpen, setIsOceanExportOpen] = useState(false);
  const [isOceanImportOpen, setIsOceanImportOpen] = useState(false);
  const [isAIAgentOpen, setIsAIAgentOpen] = useState(false);

  const activeView = location.pathname.slice(1) || "dashboard";

  const subMenuItems = [
    { id: "dashboard", label: t("aiInsights") },
    { id: "customs", label: t("customsFiling") },
    { id: "containers", label: t("containerTracking") },
    { id: "financials", label: t("financials") },
    { id: "email-summary", label: t("emailSummary") },
  ];

  const reportMenuItems = [
    { id: "quotation-report", label: t("quotationReport") },
    { id: "booking-report", label: t("bookingReport") },
    { id: "accounting-report", label: t("accountingReport") },
  ];

  const systemMaintItems = [
    { id: "maintain-vessel", label: t("vessel") },
    { id: "Commodity", label: t("commodity") },
    { id: "CountryClause", label: t("countryClause") },
    { id: "Clause", label: t("clauses") },
    { id: "ChargesCode", label: t("chargesCodeMaint") },
    { id: "port-place", label: t("Ports / Places") },
    { id: "maintain-locations", label: t("Locations") },
    { id: "Users", label: t("Users") },
  ];

  const accountingMenuItems = [
    { id: "accounting/chart-of-accounts", label: t("chartOfAccounts") },
    { id: "accounting/charge-codes", label: t("chargeCodes") },
    { id: "accounting/invoices", label: t("customerInvoices") },
    { id: "accounting/bills", label: t("vendorBills") },
    { id: "accounting/journals", label: t("journalEntries") },
    { id: "accounting/payments", label: t("payments") },
    { id: "accounting/reports", label: t("reports") },
  ];

  const oceanExportItems = [
    { id: "rate-quote", label: t("rateQuoteList") },
    { id: "booking-request-list", label: t("Booking Request") },
    { id: "booking", label: t("shipmentList") },
    { id: "booking/cancel", label: t("cancelShipment") },
  ];

  const oceanImportItems = [
    { id: "import-rate-quote", label: t("importRateQuote") },
    { id: "import-booking", label: t("importBooking") },
  ];

  const isOceanActive =
    oceanExportItems.some((i) => activeView === i.id) ||
    oceanImportItems.some((i) => activeView === i.id);

  const isAccountingActive = accountingMenuItems.some(
    (i) => activeView === i.id || location.pathname.includes("/accounting"),
  );
  const isReportsActive = reportMenuItems.some((i) => activeView === i.id);
  const isSystemMaintActive = systemMaintItems.some((i) => activeView === i.id);
  const isAIAgentActive =
    activeView === "ai-agent-setup" ||
    activeView === "ai-agent-dashboard" ||
    activeView === "ai-agent-reports";

  const isAnyOceanSubmenuOpen = isOceanExportOpen || isOceanImportOpen;

  // NestedTreeItem (deeper indent, same styling as TreeItem)
  const NestedTreeItem = ({ id, label, testId }) => {
    const isActive = activeView === id;
    return (
      <div className="relative">
        <button
          onClick={() => {
            navigate(`/${id}`);
            onNavigate();
          }}
          data-testid={testId || `nav-${id.replace("/", "-")}`}
          className={`relative w-full flex items-center gap-2 pl-12 pr-3 py-2 rounded-lg transition-all duration-150 text-sm ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover
            }`}
        >
          <span
            className={`text-xs flex-shrink-0 ${isActive ? "text-blue-400" : "text-white/20"
              }`}
          >
            —
          </span>
          <span>{label}</span>
        </button>
      </div>
    );
  };

  return (
    <>
      {/* ── AI Insights (gradient button) ── */}
      <div className="relative group mb-2">
        <button
          onClick={() => {
            if (isCollapsed) {
              navigate("/dashboard");
              onNavigate();
            } else setIsAIInsightsOpen((v) => !v);
          }}
          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all bg-gradient-to-r from-[#0A34A0] to-[#00C6FB] text-white shadow-md hover:shadow-lg hover:brightness-110"
          data-testid="nav-ai-insights"
        >
          <div className="flex items-center gap-3">
            <Brain
              className="w-[18px] h-[18px] flex-shrink-0"
              strokeWidth={2}
            />
            {!isCollapsed && (
              <span className="font-semibold text-sm">{t("aiInsights")}</span>
            )}
          </div>
          {!isCollapsed && (
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isAIInsightsOpen ? "rotate-0" : "-rotate-90"
                }`}
              strokeWidth={2.5}
            />
          )}
        </button>
        {isCollapsed && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
            {t("aiInsights")}
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
          </div>
        )}
        <AnimatePresence>
          {isAIInsightsOpen && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine>
                {subMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeView={activeView}
                    onNavigate={onNavigate}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Main nav items ── */}
      <div className="space-y-1 pt-1">
        {/* <NavItem
          id="ratematrix"
          label={t("rateMatrix")}
          icon={BarChart3}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        /> */}
        {/* RateMatrix (CargoRates) by Ashish  since 13th May 2026 */}
        <NavItem
          id="ratematrix-cargorates"
          label="RateMatrix"
          icon={BarChart3}
          activeView={activeView}
          isCollapsed={isCollapsed}
          externalLink="https://app.cargorates.ai/WebPortal/SignIn.aspx?b=Uzcvc3R0MXhaR3FCcWxVZlFqekV1dz09&d=YWl3dkJKckh1SVR1dnEzUDlSa1ZCZz09"

        />

        <NavItem
          id="rate-intelligence"
          label="Rate Intelligence"
          icon={Sparkles}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="ocean-control-tower"
          label={t("controlTower")}
          icon={Compass}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="notifications"
          label={t("notificationCenter")}
          icon={Bell}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        {/* ── Ocean ── */}
        <ParentNavBtn
          icon={Ship}
          label={t("ocean")}
          isActive={isOceanActive}
          isOpen={isOceanOpen}
          isCollapsed={isCollapsed}
          testId="nav-ocean"
          onClick={() => {
            if (isCollapsed) {
              navigate("/rate-quote");
              onNavigate();
            } else setIsOceanOpen((v) => !v);
          }}
        />
        <AnimatePresence>
          {isOceanOpen && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine={!isAnyOceanSubmenuOpen}>
                <TreeParent
                  label={t("export")}
                  isActive={oceanExportItems.some((i) => activeView === i.id)}
                  isOpen={isOceanExportOpen}
                  testId="nav-ocean-export"
                  onClick={() => setIsOceanExportOpen((v) => !v)}
                >
                  {oceanExportItems.map((item) => (
                    <NestedTreeItem key={item.id} {...item} />
                  ))}
                </TreeParent>

                <TreeParent
                  label={t("import")}
                  isActive={oceanImportItems.some((i) => activeView === i.id)}
                  isOpen={isOceanImportOpen}
                  testId="nav-ocean-import"
                  onClick={() => setIsOceanImportOpen((v) => !v)}
                >
                  {oceanImportItems.map((item) => (
                    <NestedTreeItem key={item.id} {...item} />
                  ))}
                </TreeParent>
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        <NavItem
          id="rfq"
          label={t("rfq")}
          icon={FileQuestion}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="quote-followup"
          label={t("quoteFollowup")}
          icon={MessageSquareMore}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          externalLink="https://app.mylogpal.ai/SignIn.aspx?b=aXFoYXRYNGJNMUpwdkxkY2xreDZ1dz09&d=dy9XU2RjVGdqNDVxZDFNSmlIbURjQT09"
        />
        <NavItem
          id="purchase-orders"
          label={t("purchaseOrder")}
          icon={ShoppingCart}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="sales-orders"
          label={t("salesOrder")}
          icon={ShoppingBag}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="load-plan"
          label={t("loadPlan")}
          icon={Truck}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="mailbox"
          label={t("aiAutomation")}
          icon={Sparkles}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        {/* ── AI Agent ── */}
        <ParentNavBtn
          icon={Bot}
          label={t("aiAgent")}
          isActive={isAIAgentActive}
          isOpen={isAIAgentOpen}
          isCollapsed={isCollapsed}
          testId="nav-ai-agent"
          onClick={() => {
            if (isCollapsed) {
              navigate("/ai-agent-setup");
              onNavigate();
            } else setIsAIAgentOpen((v) => !v);
          }}
        />
        <AnimatePresence>
          {!isCollapsed && isAIAgentOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine>
                <TreeItem
                  id="ai-agent-setup"
                  label={t("agentSetup")}
                  activeView={activeView}
                  onNavigate={onNavigate}
                />
                <TreeItem
                  id="ai-agent-dashboard"
                  label={t("dashboard")}
                  activeView={activeView}
                  onNavigate={onNavigate}
                />
                <TreeItem
                  id="ai-agent-reports"
                  label={t("reports")}
                  activeView={activeView}
                  onNavigate={onNavigate}
                />
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        <NavItem
          id="api-docs"
          label={t("api")}
          icon={Code2}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          externalLink="https://api.shipez.ai/swagger/index.html"
        />
        <NavItem
          id="vision-board"
          label={t("visionBoard")}
          icon={Grid3X3}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="address-book"
          label={t("addressBook")}
          icon={Users}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />
        <NavItem
          id="admin"
          label="Admin Panel"
          icon={Building2}
          activeView={activeView}
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        {/* ── Accounting ── */}
        <ParentNavBtn
          icon={Calculator}
          label={t("accounting")}
          isActive={isAccountingActive}
          isOpen={isAccountingOpen}
          isCollapsed={isCollapsed}
          testId="nav-accounting"
          onClick={() => {
            if (isCollapsed) {
              navigate("/accounting/chart-of-accounts");
              onNavigate();
            } else setIsAccountingOpen((v) => !v);
          }}
        />
        <AnimatePresence>
          {isAccountingOpen && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine>
                {accountingMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeView={activeView}
                    onNavigate={onNavigate}
                    testId={`nav-${item.id.replace("/", "-")}`}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Reports ── */}
        <ParentNavBtn
          icon={BarChart3}
          label={t("reportsMenu")}
          isActive={isReportsActive}
          isOpen={isReportsOpen}
          isCollapsed={isCollapsed}
          onClick={() => {
            if (isCollapsed) {
              navigate("/quotation-report");
              onNavigate();
            } else setIsReportsOpen((v) => !v);
          }}
        />
        <AnimatePresence>
          {isReportsOpen && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine>
                {reportMenuItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeView={activeView}
                    onNavigate={onNavigate}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── System Maintenance ── */}
        <ParentNavBtn
          icon={Settings}
          label={t("systemMaintenance")}
          isActive={isSystemMaintActive}
          isOpen={isSystemMaintOpen}
          isCollapsed={isCollapsed}
          onClick={() => {
            if (isCollapsed) {
              navigate("/maintain-vessel");
              onNavigate();
            } else setIsSystemMaintOpen((v) => !v);
          }}
        />
        <AnimatePresence>
          {isSystemMaintOpen && !isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <TreeMenu showLine>
                {systemMaintItems.map((item) => (
                  <TreeItem
                    key={item.id}
                    {...item}
                    activeView={activeView}
                    onNavigate={onNavigate}
                  />
                ))}
              </TreeMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ isOpen, onClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const masterCompanyDetailsRedux = useSelector(
    (state) => state.common?.MasterCompanyDetails,
  );

  const MasterCompanyDetail = useMemo(() => {
    const row = masterCompanyDetailsRedux?.[0];
    if (row && Object.keys(row).length > 0) return row;
    try {
      const parsed = JSON.parse(
        localStorage.getItem("MasterCompanyDetails") ?? "[]",
      );
      return parsed?.[0] ?? {};
    } catch {
      return {};
    }
  }, [masterCompanyDetailsRedux]);

  const desktopLogoSrc =
    MasterCompanyDetail?.CompanyLogo ||
    MasterCompanyDetail?.CompanyLogoDark ||
    "/shipez-logo.png";

  const isMobileView =
    typeof window !== "undefined" && window.innerWidth < 1024;
  const effectiveCollapsed = isMobileView ? false : isCollapsed;

  const handleNavigate = useCallback(() => {
    if (onClose && window.innerWidth < 1024) onClose();
  }, [onClose]);

  const sidebarBase =
    "bg-gradient-to-b from-[#0A0A0A] to-[#121212] border-r border-slate-800 flex flex-col";

  // ── Footer with version badge + collapse button (desktop only) ────────────
  const DesktopFooter = () => (
    <div className="p-3  space-y-2">
      {/* Version badge */}
      {/* <div className="bg-slate-800/80 rounded-lg px-2 py-2 flex items-center justify-center border border-white/5">
        <span
          className={`font-semibold text-lime-400 ${effectiveCollapsed ? "text-[10px]" : "text-xs"}`}
        >
          {effectiveCollapsed ? "v1" : "v1.22.25"}
        </span>
      </div> */}
      {/* Collapse toggle */}
      <button
        onClick={() => setIsCollapsed((v) => !v)}
        className="w-full flex items-center justify-center gap-2 h-9 rounded-lg
                   text-[#9CA3AF] hover:text-white hover:bg-white/5
                   border border-white/5 transition-all duration-200"
        title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <ChevronLeft
          className={`w-4 h-4 transition-transform duration-200 ${effectiveCollapsed ? "rotate-180" : ""
            }`}
          strokeWidth={2}
        />
        {!effectiveCollapsed && (
          <span className="text-[12px] font-medium">Collapse</span>
        )}
      </button>
    </div>
  );

  // ── Mobile footer — version only, no collapse button ─────────────────────
  const MobileFooter = () => (
    <div className="p-3 border-t border-slate-800">
      <div className="bg-slate-800/80 rounded-lg px-2 py-2 flex items-center justify-center border border-white/5">
        <span className="font-semibold text-lime-400 text-xs">v1.22.25</span>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Desktop Sidebar ── */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1, width: effectiveCollapsed ? 72 : 256 }}
        transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        className={`hidden lg:flex ${sidebarBase} relative flex-shrink-0 h-screen sticky top-0`}
        style={{ width: effectiveCollapsed ? 72 : 256 }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-800 flex items-center justify-center min-h-[64px]">
          <img
            src={desktopLogoSrc}
            alt={MasterCompanyDetail?.CompanyName || "Company"}
            width={effectiveCollapsed ? 32 : 160}
            height={effectiveCollapsed ? 32 : 48}
            decoding="async"
            fetchPriority="high"
            loading="eager"
            className={
              effectiveCollapsed
                ? "h-8 w-8 object-contain rounded"
                : "h-12 w-auto max-w-[160px] object-contain"
            }
          />
        </div>

        {/* Nav */}
        <nav
          className={`flex-1 min-h-0 ${effectiveCollapsed ? "p-2" : "p-3"
            } space-y-0.5 overflow-y-auto overflow-x-hidden`}
          data-testid="sidebar-nav"
        >
          <NavContent
            isCollapsed={effectiveCollapsed}
            onNavigate={handleNavigate}
          />
        </nav>

        <DesktopFooter />
      </motion.aside>

      {/* ── Mobile Sidebar (drawer) ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className={`fixed inset-y-0 left-0 z-50 w-[280px] sm:w-[300px] ${sidebarBase} lg:hidden shadow-2xl`}
            data-testid="mobile-sidebar"
          >
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <img
                src="/shipez-logo.png"
                alt="Shipez Logo"
                className="h-10 w-auto object-contain"
              />
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-[#9CA3AF]" />
              </button>
            </div>

            <nav
              className="flex-1 min-h-0 p-3 space-y-0.5 overflow-y-auto"
              data-testid="mobile-sidebar-nav"
            >
              <NavContent isCollapsed={false} onNavigate={handleNavigate} />
            </nav>

            <MobileFooter />
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
